import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    getCaptureStepLabel,
    getCaptureStepStartInstructionsImageUrl,
    getNextCaptureStepUrl
} from "@utils/capture-step-util";
import { Logger } from "@utils/logger";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { datadogRum } from "@datadog/browser-rum";
import { AuditLogLevel } from "@enums/audit-log-level";
import { setError } from "@redux/actions/error-actions";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { navigateToUtil } from "@utils/navigation-util";
import { startCaptureProcess } from "@requests/start-capture-process";
import { setIsStartingCapture, setStartCaptureSuccess } from "@redux/actions/verification-step-config-actions";
import { getCaptureStarted, getIsStartingCapture, getCaptureSteps } from "@redux/selectors/verification-step-config-selectors";
import { PrimaryButton } from "../common/primary-button/primary-button";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { AuditLogTypes } from "@constants/audit-log-types";
import styles from "./start-page.less";
import { SanitizedHtmlContainer } from "@iddataweb/idw-common-ui/containers";

export const StartPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const content = useSelector(getLanguageContent);
    const captureSteps = useSelector(getCaptureSteps);
    const queryParametersState = useSelector(getQueryParametersState);
    const isStartingCapture = useSelector(getIsStartingCapture);
    const captureStarted = useSelector(getCaptureStarted);
    const { writeAuditLog } = useAuditLogger();

    useEffect(() => {
        if (captureStarted) {
            const nextStepUrl = getNextCaptureStepUrl(captureSteps);
            navigateToUtil(nextStepUrl, navigate, location.pathname);
        }
    }, [captureStarted]);

    const onClickStartCapture = async () => {
        dispatch(setIsStartingCapture());
        Logger.info("[DocumentCapture] Starting the capture process");
        try {
            const response = await startCaptureProcess(queryParametersState);
            if (response) {
                Logger.info("[DocumentCapture] Started the capture process successfully");
                writeAuditLog(AuditLogTypes.DCUI_NEW_CAPTURE_SESSION_STARTED_SUCCESSFULLY);
                dispatch(setStartCaptureSuccess(response.responseToken));
            }
        } catch (error) {
            Logger.error("[DocumentCaptureEffects] Error starting the capture process:", error);
            const err = new Error("[DocumentCaptureEffects] Error getting the configuration");
            datadogRum.addError(err, { error });
            writeAuditLog(AuditLogTypes.DCUI_NEW_CAPTURE_SESSION_START_FAILED, AuditLogLevel.ERROR);
            console.error(error);
            dispatch(setError({
                name: "Start Capture Error",
                asi: queryParametersState.token,
                errorCode: "500",
                errorURI: location.pathname,
                httpStatus: "OK",
                message: error
            }));
            navigateToUtil("/error", navigate, location.pathname);
        }
    };

    const buttonText = useMemo(() => {
        return isStartingCapture
            ? content.start.loadingButtonText
            : content.start.buttonText;
    }, [content.start.loadingButtonText, content.start.buttonText, isStartingCapture]);

    const headline = content.start.headline;
    const title = content.start.title;
    const explanationHtml = content.start.explanationHtml;
    const subheading = content.start.subHeading;

    return (
        <div className={styles.startPage}>
            {headline && (
                <SanitizedHtmlContainer html={`<h1>${headline}</h1>`} />
            )}
            {title && (
                <SanitizedHtmlContainer html={`<p>${title}</p>`} />
            )}
            {explanationHtml && (
                <SanitizedHtmlContainer html={`<span>${explanationHtml}</span>`} className={styles.explanationHtml} />
            )}
            <p className={styles.subheading}>{subheading}</p>
            {captureSteps.map(captureStep => (
                <div className={styles.stepContainer} key={captureStep.id}>
                    <img src={getCaptureStepStartInstructionsImageUrl(captureStep.type, content)} />
                    <label>{getCaptureStepLabel(captureStep.type, content)}</label>
                </div>
            ))}
            <div className={styles.startButtonContainer}>
                <PrimaryButton
                    id="dcui-start-button"
                    disabled={isStartingCapture || captureStarted}
                    label={buttonText}
                    type={"button"}
                    onClick={onClickStartCapture}
                />
            </div>
        </div>
    );
};